/**
 * V20 — Consolidated Print Preview Page (headless render target)
 *
 * Route: /print-preview-consolidated/:groupId
 * Query params: same as /print-preview (?package, ?report, ?section, ?token)
 *
 * Consumed by the Playwright subprocess spawned by
 * POST /api/pdf/generate-consolidated. Validates the render token, fetches
 * the consolidated envelope via /api/consolidated/{group_id}/data with
 * the backend-issued service JWT, builds the consolidated reports, then
 * mounts PrintModal in headless mode with `consolidatedReports` so the
 * package renders identically to the on-screen path.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PrintModal from '../components/PrintModal';
import { buildConsolidated } from '../lib/reports';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const consumeRenderToken = (token) =>
  axios.post(`${API_URL}/pdf/render-token/consume`, { token });

const auth = (jwt) => ({ headers: { Authorization: `Bearer ${jwt}` } });

export default function PrintPreviewConsolidatedPage() {
  const { groupId } = useParams();
  const [search] = useSearchParams();
  const pkg = search.get('package') || 'package';
  const reportType = search.get('report') || 'prepared';
  const section = search.get('section') || 'all';
  const token = search.get('token') || '';

  const [envelope, setEnvelope] = useState(null);
  const [group, setGroup] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Missing render token.');
      return;
    }
    let cancelled = false;

    // V24 — match on-screen preview: apply compact `.mockup-print` styling
    // to the headless consolidated Export/Print PDF as well.
    document.body.classList.add('pdf-server', 'mockup-print');
    document.body.classList.add(`section-${section}`);
    document.body.removeAttribute('data-render-ready');

    // Hide Emergent platform badge from headless PDF render.
    const hideBadgeStyle = document.createElement('style');
    hideBadgeStyle.id = 'pdf-server-hide-emergent-badge';
    hideBadgeStyle.textContent = '#emergent-badge { display: none !important; visibility: hidden !important; }';
    document.head.appendChild(hideBadgeStyle);
    const dropBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
    dropBadge();
    const badgeInterval = setInterval(dropBadge, 200);
    setTimeout(() => clearInterval(badgeInterval), 2500);

    (async () => {
      try {
        const tokResp = await consumeRenderToken(token);
        const { service_jwt, group_id: scopedGroupId } = tokResp.data;
        if (scopedGroupId !== groupId) {
          throw new Error('Token scope mismatch.');
        }
        const [groupResp, envResp] = await Promise.all([
          axios.get(`${API_URL}/clients/${groupId}`, auth(service_jwt)).catch(() => null),
          axios.get(`${API_URL}/consolidated/${groupId}/data`, auth(service_jwt)).catch((e) => {
            console.error('consolidated fetch', e?.response?.status, e?.message);
            return null;
          }),
        ]);
        if (cancelled) return;
        if (!envResp) throw new Error('Failed to load consolidated envelope.');
        setGroup(groupResp?.data || null);
        setEnvelope(envResp.data);
      } catch (e) {
        console.error('Consolidated PrintPreview load failed:', e);
        if (!cancelled) setError(e.message || String(e));
      }
    })();

    return () => { cancelled = true; };
  }, [groupId, token, section]);

  // Build consolidated reports once envelope is ready
  const consolidated = React.useMemo(() => {
    if (!envelope?.members) return null;
    const memberPayloads = envelope.members
      .filter((m) => m.accounts_payload && m.mapping && !m.accounts_error)
      .map((m) => ({
        client: m.client,
        accounts: m.accounts_payload.accounts || [],
        mapping: m.mapping,
        ttm: m.ttm,
        qboMeta: {
          ni_at_period_start: m.accounts_payload.ni_at_period_start,
          cash_at_period_start: m.accounts_payload.cash_at_period_start,
        },
      }));
    if (memberPayloads.length === 0) return null;
    const firstTtm = envelope.members.find((m) => m.ttm?.months);
    const ttmMonthLabels = firstTtm
      ? (firstTtm.ttm.months || []).map((m) => `${m.month} ${String(m.year).slice(-2)}`)
      : [];
    return buildConsolidated(memberPayloads, ttmMonthLabels);
  }, [envelope]);

  // Signal readiness to Playwright once reports are built.
  useEffect(() => {
    if (consolidated && group) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.body.setAttribute('data-render-ready', '1');
        });
      });
    }
  }, [consolidated, group]);

  if (error) {
    return (
      <div style={{ padding: 40, fontFamily: 'monospace' }}>
        <h2>Consolidated Print Preview Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!consolidated || !group) {
    return (
      <div style={{ padding: 20, fontSize: 12, color: '#666' }}>
        Loading consolidated package…
      </div>
    );
  }

  // Synthetic "client" object so PrintModal renders masthead correctly.
  const printClient = {
    id: group.id,
    company: group.company,
    name: group.name,
    framework: group.framework || 'gaap',
    framework_other_label: group.framework_other_label,
    engagement_type: group.engagement_type || 'preparation',
    disclosures_omitted: group.disclosures_omitted ?? true,
    independent: group.independent ?? true,
    cf_materiality_threshold: group.cf_materiality_threshold,
    bs_py_comparison_basis: group.bs_py_comparison_basis || 'prior_year_same_date',
    entity_type: 'consolidated',
  };

  const qboMeta = { period_end: envelope.period_end, period_start: envelope.period_start };

  return (
    <div data-testid="print-preview-consolidated-root">
      <PrintModal
        isOpen={true}
        onClose={() => {}}
        accounts={[]}
        client={printClient}
        currentStatement="pl"
        ttmData={null}
        mapping={consolidated?.members?.[0]?.mapping || {}}
        qboMeta={qboMeta}
        userRole="staff"
        headless={true}
        section={section}
        forcePackage={pkg}
        forceReportType={reportType}
        consolidatedReports={consolidated}
        consolidatedMembers={(consolidated.members || []).map((m) => m.client)}
      />
    </div>
  );
}
